/**
 * API Configuration
 * Centralized configuration for microservices architecture
 * Supports three distinct microservices:
 * 1. CredentialingAPI - NPI validation and credentialing operations
 * 2. Auth API - Azure Entra External ID integration and authentication
 * 3. Data Access API - CRUD operations for forms, documents, and other data
 */

/**
 * Normalize environment variable values by trimming whitespace and treating empty strings as undefined.
 */
const normalizeEnvValue = (value: string | undefined | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Get the base URL for API requests
 * Uses the configured VITE_API_BASE_URL environment variable
 * Falls back to default if not configured
 */
const getBaseUrl = (): string => {
  const configuredUrl = normalizeEnvValue(import.meta.env.VITE_API_BASE_URL);
  
  // Always use the configured URL if provided, otherwise use default
  return configuredUrl ?? 'https://your-api.azure-api.net';
};

export const API_CONFIG = {
  baseUrl: getBaseUrl(),
  apiVersion: 'v1',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
} as const;

/**
 * ============================================================================
 * CREDENTIALING API CONFIGURATION
 * ============================================================================
 * Microservice: CredentialingAPI
 * Purpose: NPI validation and credentialing operations
 * Environment Variable: VITE_CREDENTIALING_API_BASE_URL
 */
const getCredentialingApiBaseUrl = (): string => {
  // Use credentialing API base URL
  const configuredUrl = normalizeEnvValue(import.meta.env.VITE_CREDENTIALING_API_BASE_URL);
  
  if (!configuredUrl && import.meta.env.DEV) {
    console.warn('[CredentialingAPI] VITE_CREDENTIALING_API_BASE_URL is not configured.');
  }
  
  return configuredUrl ?? 'https://credentialing-api.azure-api.net';
};

export const CREDENTIALING_API_CONFIG = {
  baseUrl: getCredentialingApiBaseUrl(),
  apiVersion: import.meta.env.VITE_CREDENTIALING_API_VERSION ?? 'v1',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
  // Credentialing API key for service-to-service authentication
  apiKey: import.meta.env.VITE_CREDENTIALING_API_KEY,
  apiKeyHeader: import.meta.env.VITE_CREDENTIALING_API_KEY_HEADER ?? 'X-API-Key',
  // NPI Validation API key (can be different from credentialing API key)
  npiValidationApiKey: import.meta.env.VITE_NPI_VALIDATION_API_KEY ?? import.meta.env.VITE_CREDENTIALING_API_KEY,
  npiValidationApiKeyHeader: import.meta.env.VITE_NPI_VALIDATION_API_KEY_HEADER ?? 'X-API-KEY',
  // Skip NPI validation API call in development (dev purposes only)
  skipNpiValidation: import.meta.env.DEV && import.meta.env.VITE_SKIP_NPI_VALIDATION === 'true',
} as const;

/**
 * ============================================================================
 * DATA ACCESS API CONFIGURATION
 * ============================================================================
 * Microservice: Data Access API
 * Purpose: CRUD operations for forms, documents, and other data entities
 * Environment Variable: VITE_DATA_ACCESS_API_BASE_URL
 */
const getDataAccessApiBaseUrl = (): string => {
  const configuredUrl = import.meta.env.VITE_DATA_ACCESS_API_BASE_URL;
  
  if (!configuredUrl && import.meta.env.DEV) {
    console.warn('[DataAccessAPI] VITE_DATA_ACCESS_API_BASE_URL is not configured. Falling back to CredentialingAPI base URL.');
  }
  
  return configuredUrl ?? 'https://data-access-api.azure-api.net';
};

export const DATA_ACCESS_API_CONFIG = {
  baseUrl: getDataAccessApiBaseUrl(),
  apiVersion: import.meta.env.VITE_DATA_ACCESS_API_VERSION ?? 'v1',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
  // Data Access API key for service-to-service authentication
  apiKey: import.meta.env.VITE_DATA_ACCESS_API_KEY,
  apiKeyHeader: import.meta.env.VITE_DATA_ACCESS_API_KEY_HEADER ?? 'X-API-Key',
} as const;

/**
 * ============================================================================
 * USER API CONFIGURATION
 * ============================================================================
 * Microservice: User API
 * Purpose: User registration and profile management operations
 * Environment Variable: VITE_API_USER_BASE_URL
 */
const getUserApiBaseUrl = (): string => {
  const configuredUrl = normalizeEnvValue(import.meta.env.VITE_API_USER_BASE_URL);
  
  if (!configuredUrl && import.meta.env.DEV) {
    console.warn('[UserAPI] VITE_API_USER_BASE_URL is not configured.');
  }
  
  return configuredUrl ?? 'https://localhost:7006';
};

export const USER_API_CONFIG = {
  baseUrl: getUserApiBaseUrl(),
  apiVersion: import.meta.env.VITE_USER_API_VERSION ?? 'v1',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
  // User API key for service-to-service authentication (optional)
  apiKey: import.meta.env.VITE_USER_API_KEY,
  apiKeyHeader: import.meta.env.VITE_USER_API_KEY_HEADER ?? 'X-API-Key',
} as const;


/**
 * Helper function to build versioned API paths for CredentialingAPI
 * @param path - API path without version prefix (e.g., '/NpiValidation')
 * @returns Versioned API path (e.g., '/api/v1/NpiValidation')
 */
export const getCredentialingApiPath = (path: string): string => {
  return `${CREDENTIALING_API_CONFIG.baseUrl}/api/${CREDENTIALING_API_CONFIG.apiVersion}${path}`;
};

/**
 * Build NPI Validation endpoint path
 * Uses VITE_CREDENTIALING_API_NPI_VALIDATION_ENDPOINT if set (can be full URL, full path, or relative)
 * Otherwise constructs from base URL, API version, and default path
 * @returns Endpoint path (e.g., '/api/v1/NpiValidation' or custom path)
 */
const getNpiValidationEndpoint = (): string => {
  const customEndpoint = import.meta.env.VITE_CREDENTIALING_API_NPI_VALIDATION_ENDPOINT;
  
  if (customEndpoint) {
    // If custom endpoint is provided, check if it's a full URL or just a path
    if (customEndpoint.startsWith('http://') || customEndpoint.startsWith('https://')) {
      // Absolute URL - extract path relative to base URL
      try {
        const endpointUrl = new URL(customEndpoint);
        return endpointUrl.pathname;
      } catch {
        // Invalid URL, treat as path
        return customEndpoint.startsWith('/') ? customEndpoint : `/${customEndpoint}`;
      }
    }
    // Ensure it starts with / for relative paths
    return customEndpoint.startsWith('/') ? customEndpoint : `/${customEndpoint}`;
  }
  
  // Default: construct from API version (baseUrl has no path or is not a valid URL)
  return getCredentialingApiPath('/NpiValidation');
};

/**
 * Helper function to build versioned API paths for Data Access API
 * @param path - API path without version prefix (e.g., '/forms')
 * @returns Versioned API path (e.g., '/api/v1/forms')
 */
export const getDataAccessApiPath = (path: string): string => {
  return `/api/${DATA_ACCESS_API_CONFIG.apiVersion}${path}`;
};

/**
 * ============================================================================
 * CREDENTIALING API ENDPOINTS
 * ============================================================================
 */
export const CREDENTIALING_API_ENDPOINTS = {
  health: import.meta.env.VITE_CREDENTIALING_API_HEALTH_ENDPOINT ?? getCredentialingApiPath('/health'),
  npiValidation: getNpiValidationEndpoint(),
} as const;

/**
 * ============================================================================
 * DATA ACCESS API ENDPOINTS
 * ============================================================================
 */
export const DATA_ACCESS_API_ENDPOINTS = {
  documents: import.meta.env.VITE_DATA_ACCESS_API_DOCUMENTS_ENDPOINT ?? getDataAccessApiPath('/documents'),
  forms: import.meta.env.VITE_DATA_ACCESS_API_FORMS_ENDPOINT ?? getDataAccessApiPath('/forms'),
} as const;

/**
 * ============================================================================
 * AUTH API CONFIGURATION
 * ============================================================================
 * Microservice: Auth API
 * Purpose: Azure Entra External ID integration and authentication operations
 * Environment Variable: VITE_AUTH_SERVICE_URL
 */
// Helper to get provider type with debugging
const getProviderType = (): 'rest' | 'oauth2' | 'msal' => {
  const envValue = import.meta.env.VITE_AUTH_PROVIDER_TYPE;
  const providerType = (envValue as 'rest' | 'oauth2' | 'msal') ?? 'rest';
  
  return providerType;
};

const getAuthApiBaseUrl = (): string => {
  const configuredUrl = import.meta.env.VITE_AUTH_SERVICE_URL;
  
  if (!configuredUrl) {
    if (import.meta.env.DEV) {
      console.warn('[AuthAPI] VITE_AUTH_SERVICE_URL is not configured. Auth operations may fail.');
    }
    return 'https://auth-api.azure-api.net';
  }
  return configuredUrl;
};

export const AUTH_API_CONFIG = {
  baseUrl: getAuthApiBaseUrl(),
  apiVersion: import.meta.env.VITE_AUTH_API_VERSION ?? 'v1',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
  // Provider type: 'rest' | 'oauth2' | 'msal'
  providerType: getProviderType(),
  // Auth API key for service-to-service authentication
  apiKey: import.meta.env.VITE_AUTH_API_KEY,
  apiKeyHeader: import.meta.env.VITE_AUTH_API_KEY_HEADER ?? 'X-API-Key',
} as const;

/**
 * ============================================================================
 * AUTH API ENDPOINTS
 * ============================================================================
 */
export const AUTH_API_ENDPOINTS = {
  login: import.meta.env.VITE_AUTH_LOGIN_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/auth/login`,
  refresh: import.meta.env.VITE_AUTH_REFRESH_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/auth/refresh`,
  logout: import.meta.env.VITE_AUTH_LOGOUT_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/auth/logout`,
  introspect: import.meta.env.VITE_AUTH_INTROSPECT_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/auth/introspect`,
  register: import.meta.env.VITE_AUTH_REGISTER_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/auth/register`,
  // User profile endpoints
  userProfile: import.meta.env.VITE_AUTH_USER_PROFILE_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/user/profile`,
  userClaims: import.meta.env.VITE_AUTH_USER_CLAIMS_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/user/claims`,
  userVerify: import.meta.env.VITE_AUTH_USER_VERIFY_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/user/verify`,
  userEntra: import.meta.env.VITE_AUTH_USER_ENTRA_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/user/entra`,
  // Session endpoints
  sessionCreate: import.meta.env.VITE_AUTH_SESSION_CREATE_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/session/create`,
  sessionCurrent: import.meta.env.VITE_AUTH_SESSION_CURRENT_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/session/current`,
  sessionHeartbeat: import.meta.env.VITE_AUTH_SESSION_HEARTBEAT_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/session/heartbeat`,
  sessionTerminate: import.meta.env.VITE_AUTH_SESSION_TERMINATE_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/session/terminate`,
  sessionValidate: import.meta.env.VITE_AUTH_SESSION_VALIDATE_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/session/validate`,
  sessionActive: import.meta.env.VITE_AUTH_SESSION_ACTIVE_ENDPOINT ?? `/api/${AUTH_API_CONFIG.apiVersion}/session/active`,
  registrationStageSignupAttributes:
    import.meta.env.VITE_AUTH_REGISTRATION_STAGE_ATTRIBUTES_ENDPOINT ??
    `/api/${AUTH_API_CONFIG.apiVersion}/registration/stage-signup-attributes`,
} as const;

/**
 * ============================================================================
 * SERVICE-TO-SERVICE AUTHENTICATION CONFIGURATION
 * ============================================================================
 */
export const SERVICE_AUTH_CONFIG = {
  enabled: import.meta.env.VITE_SERVICE_AUTH_ENABLED === 'true',
  apiKey: import.meta.env.VITE_SERVICE_API_KEY,
  apiKeyHeader: import.meta.env.VITE_SERVICE_API_KEY_HEADER ?? 'X-API-Key',
  serviceToken: import.meta.env.VITE_SERVICE_TOKEN,
} as const;

export const CDN_CONFIG = {
  baseUrl: import.meta.env.VITE_CDN_BASE_URL ?? 'https://your-cdn.azureedge.net',
} as const;

/**
 * ============================================================================
 * HELPER FUNCTIONS
 * ============================================================================
 */

/**
 * Get full CredentialingAPI endpoint URL
 */
export const getCredentialingApiUrl = (endpoint: string): string => {
  return `${CREDENTIALING_API_CONFIG.baseUrl}${endpoint}`;
};

/**
 * Get full Data Access API endpoint URL
 */
export const getDataAccessApiUrl = (endpoint: string): string => {
  return `${DATA_ACCESS_API_CONFIG.baseUrl}${endpoint}`;
};

/**
 * Get full Auth API endpoint URL
 */
export const getAuthApiUrl = (endpoint: string): string => {
  return `${AUTH_API_CONFIG.baseUrl}${endpoint}`;
};

/**
 * Get full CaseMgmtService API endpoint URL
 */
export const getCaseMgmtServiceApiUrl = (endpoint: string): string => {
  return `${CASE_MGMT_SERVICE_API_CONFIG.baseUrl}${endpoint}`;
};


/**
 * Get CDN URL for JSON resources
 */
export const getCdnUrl = (resourcePath: string): string => {
  return `${CDN_CONFIG.baseUrl}/${resourcePath}`;
};

/**
 * ============================================================================
 * CASE MANAGEMENT SERVICE API CONFIGURATION
 * ============================================================================
 * Microservice: CaseMgmtService
 * Purpose: Exam form details and case management operations
 * Environment Variable: VITE_CASE_MGMT_SERVICE_API_BASE_URL
 */
const getCaseMgmtServiceApiBaseUrl = (): string => {
  const configuredUrl = normalizeEnvValue(import.meta.env.VITE_CASE_MGMT_SERVICE_API_BASE_URL);

  if (!configuredUrl && import.meta.env.DEV) {
    console.warn('[CaseMgmtService] VITE_CASE_MGMT_SERVICE_API_BASE_URL is not configured.');
  }

  return configuredUrl ?? 'https://localhost:7093';
};

/**
 * Parse a positive-integer millisecond env override; falls back to `fallbackMs` when the
 * env var is unset, blank, or not a positive number.
 */
const getEnvTimeoutMs = (value: string | undefined, fallbackMs: number): number => {
  const normalized = normalizeEnvValue(value);
  if (!normalized) return fallbackMs;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackMs;
};

export const CASE_MGMT_SERVICE_API_CONFIG = {
  baseUrl: getCaseMgmtServiceApiBaseUrl(),
  apiVersion: import.meta.env.VITE_CASE_MGMT_SERVICE_API_VERSION ?? 'v1',
  timeout: 30000,
  /**
   * `flowable/queue` can page through a large Flowable work queue (PAGE_SIZE up to 1000,
   * see FlowableQueueQueryLimit) and is polled every 30s — the default 30s client timeout was
   * cancelling it before the backend/Flowable engine could respond. Longer, dedicated timeout,
   * overridable via VITE_FLOWABLE_QUEUE_API_TIMEOUT_MS for further tuning per environment.
   */
  flowableQueueTimeout: getEnvTimeoutMs(
    import.meta.env.VITE_FLOWABLE_QUEUE_API_TIMEOUT_MS,
    90000
  ),
} as const;

/**
 * CaseMgmtService API Endpoints
 */
export const CASE_MGMT_SERVICE_API_ENDPOINTS = {
  dashboardExams: () => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/dashboard`,
  examFormDetails: (examFormId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exam-forms/${examFormId}/details`,
  examFormHeader: (examId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/form-header`,
  /** Associate a form with an exam (status = NEW). Backend contract TBD. */
  addFormToExam: (examId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/forms`,
  /** Get all exam forms for an exam (for removal scope and existing forms list). */
  examFormsByExamId: (examId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/exam-forms`,
  /** Deactivate (soft delete) exam forms triggered by a field. Request body: { triggerSourceFieldId }. */
  deleteTriggeredFormsByExamId: (examId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/forms/triggered`,
  /**
   * Flowable-direct account status (no API version segment).
   * CaseMgmtService FlowableDirectController: GET /api/flowable/status?account=
   */
  flowableStatus: () => '/api/flowable/status',
  /**
   * Flowable-direct start (no API version segment).
   * CaseMgmtService FlowableDirectController: POST /api/flowable/start
   */
  flowableStart: () => '/api/flowable/start',
  /**
   * Flowable-direct complete task (no API version segment).
   * CaseMgmtService FlowableDirectController: POST /api/flowable/complete-task
   */
  flowableCompleteTask: () => '/api/flowable/complete-task',
  /**
   * Flowable-direct claim task (no API version segment).
   * CaseMgmtService FlowableDirectController: POST /api/flowable/claim-task
   */
  flowableClaimTask: () => '/api/flowable/claim-task',
  /**
   * Flowable-direct process completion (no API version segment).
   * CaseMgmtService FlowableDirectController: GET /api/flowable/completion?account=
   */
  flowableCompletion: () => '/api/flowable/completion',
  /**
   * Flowable-direct work queue (no API version segment).
   * CaseMgmtService FlowableDirectController: GET /api/flowable/queue
   */
  flowableQueue: () => '/api/flowable/queue',
  /**
   * Combined Flowable queue + member data in one call (no API version segment).
   * CaseMgmtService FlowableDirectController: GET /api/flowable/workflow-queue
   */
  workflowQueue: () => '/api/flowable/workflow-queue',
  /**
   * Flowable-direct historic activity (no API version segment).
   * CaseMgmtService FlowableDirectController: GET /api/flowable/activity
   */
  flowableActivity: () => '/api/flowable/activity',
  /** PHA members projected from dow tables (catalog-shaped). */
  dowMembers: () => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/dow/members`,
  dowMemberByAccount: (account: string) =>
    `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/dow/members/${encodeURIComponent(account)}`,
  dowServiceMembers: () => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/dow/service-members`,
  dowServiceMemberByExaminee: (examineeProfileId: number) =>
    `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/dow/service-members/${examineeProfileId}`,
  dowCatalogSeed: () => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/dow/catalog/seed`,
} as const;

/**
 * ============================================================================
 * CASE MANAGEMENT PORTAL CONFIGURATION
 * ============================================================================
 * URL for Case Management Portal (for cross-portal navigation)
 * Environment Variable: VITE_CASE_MGMT_PORTAL_URL
 */
export const CASE_MGMT_PORTAL_URL = 
  normalizeEnvValue(import.meta.env.VITE_CASE_MGMT_PORTAL_URL) ?? 
  'http://localhost:5174';

/**
 * ============================================================================
 * FORM ENGINE PORTAL CONFIGURATION
 * ============================================================================
 * Base URL for Form Engine Portal (FEP). Exam forms and /form / /examform / /view routes always use FEP;
 * there is no toggle to load forms inside Healthcare Portal.
 * Environment Variable: VITE_FORM_ENGINE_PORTAL_URL
 */
export const FORM_ENGINE_PORTAL_URL =
  normalizeEnvValue(import.meta.env.VITE_FORM_ENGINE_PORTAL_URL) ??
  'http://localhost:8080';
